/**
 * Before/After Image Comparison Slider
 * Lightweight, accessible, and performant slider implementation
 */

class BeforeAfterSlider {
  constructor(element) {
    this.slider = element;
    this.container = element.querySelector('.before-after-content__slider-container');
    this.afterImage = element.querySelector('.before-after-content__slider-image--after');
    this.control = element.querySelector('.before-after-content__slider-control');
    this.handle = element.querySelector('.before-after-content__slider-handle');

    // Get settings from data attributes
    this.defaultPosition = parseFloat(element.dataset.defaultPosition) || 50;

    // State
    this.isDragging = false;
    this.currentPosition = this.defaultPosition;

    // Bind methods
    this.handleMouseDown = this.handleMouseDown.bind(this);
    this.handleMouseMove = this.handleMouseMove.bind(this);
    this.handleMouseUp = this.handleMouseUp.bind(this);
    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchMove = this.handleTouchMove.bind(this);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);

    // Initialize
    this.init();
  }

  init() {
    // Set initial position
    this.setPosition(this.defaultPosition);

    // Add event listeners
    this.control.addEventListener('mousedown', this.handleMouseDown);
    this.control.addEventListener('touchstart', this.handleTouchStart, { passive: false });
    this.control.addEventListener('keydown', this.handleKeyDown);

    // Prevent image dragging
    const images = this.slider.querySelectorAll('.before-after-content__slider-img');
    images.forEach(img => {
      img.addEventListener('dragstart', (e) => e.preventDefault());
    });

    // Update ARIA attributes
    this.updateAriaAttributes();
  }

  setPosition(percentage) {
    // Clamp between 0 and 100
    percentage = Math.max(0, Math.min(100, percentage));
    this.currentPosition = percentage;

    // Horizontal slider
    this.control.style.left = `${percentage}%`;
    // this.afterImage.style.clipPath = `inset(0 ${100 - percentage}% 0 0)`;
    this.afterImage.style.clipPath = `inset(0 ${100 - percentage}% 0 0 round 0px)`;

    // Update ARIA value
    this.control.setAttribute('aria-valuenow', Math.round(percentage));
  }

  getPositionFromEvent(event) {
    const rect = this.container.getBoundingClientRect();
    const x = (event.clientX || event.touches[0].clientX) - rect.left;
    const percentage = (x / rect.width) * 100;
    return percentage;
  }

  handleMouseDown(event) {
    event.preventDefault();
    this.isDragging = true;
    this.control.style.cursor = 'ew-resize';
    document.addEventListener('mousemove', this.handleMouseMove);
    document.addEventListener('mouseup', this.handleMouseUp);
    this.setPosition(this.getPositionFromEvent(event));
  }

  // handleMouseMove(event) {
  //   if (!this.isDragging) return;
  //   event.preventDefault();
  //   this.setPosition(this.getPositionFromEvent(event));
  // }

  handleMouseMove(event) {
    if (!this.isDragging) return;
    event.preventDefault();

    const position = this.getPositionFromEvent(event);

    if (!this.rafPending) {
      this.rafPending = true;
      requestAnimationFrame(() => {
        this.setPosition(position);
        this.rafPending = false;
      });
    }
  }

  handleMouseUp(event) {
    if (!this.isDragging) return;
    this.isDragging = false;
    this.control.style.cursor = '';
    document.removeEventListener('mousemove', this.handleMouseMove);
    document.removeEventListener('mouseup', this.handleMouseUp);
  }

  handleTouchStart(event) {
    event.preventDefault();
    this.isDragging = true;
    document.addEventListener('touchmove', this.handleTouchMove, { passive: false });
    document.addEventListener('touchend', this.handleTouchEnd);
    this.setPosition(this.getPositionFromEvent(event));
  }

  // handleTouchMove(event) {
  //   if (!this.isDragging) return;
  //   event.preventDefault();
  //   this.setPosition(this.getPositionFromEvent(event));
  // }

  handleTouchMove(event) {
    if (!this.isDragging) return;
    event.preventDefault();

    const position = this.getPositionFromEvent(event);

    if (!this.rafPending) {
      this.rafPending = true;
      requestAnimationFrame(() => {
        this.setPosition(position);
        this.rafPending = false;
      });
    }
  }

  handleTouchEnd(event) {
    if (!this.isDragging) return;
    this.isDragging = false;
    document.removeEventListener('touchmove', this.handleTouchMove);
    document.removeEventListener('touchend', this.handleTouchEnd);
  }

  handleKeyDown(event) {
    let newPosition = this.currentPosition;
    const step = 5; // 5% increments

    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        newPosition = this.currentPosition - step;
        break;
      case 'ArrowRight':
        event.preventDefault();
        newPosition = this.currentPosition + step;
        break;
      case 'Home':
        event.preventDefault();
        newPosition = 0;
        break;
      case 'End':
        event.preventDefault();
        newPosition = 100;
        break;
      default:
        return;
    }

    this.setPosition(newPosition);
  }

  updateAriaAttributes() {
    this.control.setAttribute('aria-label', 'Drag to compare before and after images');
    this.control.setAttribute('aria-valuemin', '0');
    this.control.setAttribute('aria-valuemax', '100');
    this.control.setAttribute('aria-valuenow', Math.round(this.currentPosition));
    this.control.setAttribute('role', 'slider');
    this.control.setAttribute('tabindex', '0');
  }

  destroy() {
    // Clean up event listeners
    this.control.removeEventListener('mousedown', this.handleMouseDown);
    this.control.removeEventListener('touchstart', this.handleTouchStart);
    this.control.removeEventListener('keydown', this.handleKeyDown);
    document.removeEventListener('mousemove', this.handleMouseMove);
    document.removeEventListener('mouseup', this.handleMouseUp);
    document.removeEventListener('touchmove', this.handleTouchMove);
    document.removeEventListener('touchend', this.handleTouchEnd);
  }
}

// Initialize all sliders on page load
function initBeforeAfterSliders() {
  const sliders = document.querySelectorAll('.before-after-content__slider');
  const sliderInstances = [];

  sliders.forEach(slider => {
    // Check if already initialized
    if (slider.dataset.initialized === 'true') {
      return;
    }

    try {
      const instance = new BeforeAfterSlider(slider);
      slider.dataset.initialized = 'true';
      sliderInstances.push(instance);
    } catch (error) {
      console.error('Error initializing before/after slider:', error);
    }
  });

  return sliderInstances;
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initBeforeAfterSliders);
} else {
  initBeforeAfterSliders();
}

// Re-initialize when Shopify theme editor loads sections
if (typeof document.addEventListener !== 'undefined') {
  document.addEventListener('shopify:section:load', function (event) {
    // Only re-initialize if the loaded section contains our slider
    const section = event.detail.sectionId
      ? document.querySelector(`.section-${event.detail.sectionId}`)
      : null;

    if (section) {
      const sliders = section.querySelectorAll('.before-after-content__slider');
      sliders.forEach(slider => {
        slider.dataset.initialized = 'false';
      });
      initBeforeAfterSliders();
    }
  });
}

// Export for potential external use
if (typeof module !== 'undefined' && module.exports) {
  module.exports = BeforeAfterSlider;
}

